// frontend/src/context/GoogleMapsProvider.jsx
import React, { createContext, useContext, useState, useEffect } from "react";

const GoogleMapsContext = createContext();

export const useGoogleMaps = () => {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    throw new Error("useGoogleMaps must be used within a GoogleMapsProvider");
  }
  return context;
};

const GoogleMapsProvider = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isApiKeySet, setIsApiKeySet] = useState(false);

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    // Check if API key is set
    if (!GOOGLE_MAPS_API_KEY) {
      console.warn(
        "REACT_APP_GOOGLE_MAPS_API_KEY is not set in environment variables. Using mock mode.",
      );
      setIsApiKeySet(false);
      setIsLoaded(true);
      return;
    }

    setIsApiKeySet(true);

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      return;
    }

    // Load Google Maps script manually
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;

    // Create a global callback for the script
    window.initGoogleMaps = () => {
      setIsLoaded(true);
      delete window.initGoogleMaps;
    };

    script.onerror = () => {
      console.error("Failed to load Google Maps API");
      setLoadError("Failed to load Google Maps API");
      setIsLoaded(true); // Still mark as loaded to prevent hanging
      delete window.initGoogleMaps;
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup
      const scriptElement = document.querySelector(
        `script[src*="maps.googleapis.com"]`,
      );
      if (scriptElement) {
        document.head.removeChild(scriptElement);
      }
      if (window.initGoogleMaps) {
        delete window.initGoogleMaps;
      }
    };
  }, [GOOGLE_MAPS_API_KEY]);

  // Mock geocoding function
  const geocodeAddress = async (address) => {
    if (!address) return null;

    // Use mock if no API key or not loaded
    if (!isApiKeySet || !isLoaded) {
      return {
        lat: -26.2041,
        lng: 28.0473,
        formatted_address: address || "Johannesburg, South Africa",
      };
    }

    if (window.google && window.google.maps) {
      try {
        const geocoder = new window.google.maps.Geocoder();
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ address }, (results, status) => {
            if (status === "OK" && results && results.length > 0) {
              resolve({
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng(),
                formatted_address: results[0].formatted_address,
              });
            } else {
              reject(new Error(`Geocoding failed: ${status}`));
            }
          });
        });
        return result;
      } catch (error) {
        console.error("Geocoding error:", error);
        return {
          lat: -26.2041,
          lng: 28.0473,
          formatted_address: address || "Johannesburg, South Africa",
        };
      }
    }

    return {
      lat: -26.2041,
      lng: 28.0473,
      formatted_address: address || "Johannesburg, South Africa",
    };
  };

  // Mock autocomplete function
  const getPlacePredictions = async (input) => {
    if (!input || input.length < 2) return [];

    // Use mock if no API key or not loaded
    if (!isApiKeySet || !isLoaded) {
      return [
        {
          description: `${input} - Pretoria, South Africa`,
          place_id: `mock_${Date.now()}_1`,
        },
        {
          description: `${input} - Johannesburg, South Africa`,
          place_id: `mock_${Date.now()}_2`,
        },
        {
          description: `${input} - Cape Town, South Africa`,
          place_id: `mock_${Date.now()}_3`,
        },
      ];
    }

    if (window.google && window.google.maps) {
      try {
        const autocompleteService =
          new window.google.maps.places.AutocompleteService();
        const result = await new Promise((resolve, reject) => {
          autocompleteService.getPlacePredictions(
            { input, componentRestrictions: { country: "ZA" } },
            (predictions, status) => {
              if (status === "OK" && predictions) {
                resolve(predictions);
              } else {
                reject(new Error(`Autocomplete failed: ${status}`));
              }
            },
          );
        });
        return result;
      } catch (error) {
        console.error("Autocomplete error:", error);
        return [];
      }
    }

    return [];
  };

  return (
    <GoogleMapsContext.Provider
      value={{
        isLoaded,
        loadError,
        geocodeAddress,
        getPlacePredictions,
        isApiKeySet,
      }}
    >
      {children}
    </GoogleMapsContext.Provider>
  );
};

export default GoogleMapsProvider;
